/**
 * CREATIVE DETONATOR - IDEA CANVAS (Inline Detail View)
 * Replaces grid content when viewing an idea - NOT a modal overlay
 *
 * Features:
 * - Hides the grid, shows canvas in same location
 * - Back button returns to grid (restores scroll position)
 * - Large image on left, details on right
 * - All action buttons work via event delegation
 * - Works in both Dashboard and Generator contexts
 * - Integrated storyboard tabs (SCENES, PANELS, VIDEOS)
 *
 * Usage:
 *   IdeaCanvas.init('ideas-grid', 'canvas-view');
 *   IdeaCanvas.open(ideaObject);
 *   IdeaCanvas.close();
 *
 * @version 34 - Added Refine Idea 2.0 with modes, preview, and format conversion
 * @version 33 - Fixed auth: save/delete now use apiRequest for proper auth headers
 * @version 32 - Fixed API routing: save/delete now use API_BASE for correct port (3000 vs 8080)
 *              - Fixed copy dropdown: updated import to v=65, calls initCopyPromptDropdown on open
 * @version 31 - Fixed copy prompt: calls openCopyDropdown directly with idea+brief
 *              - Removed redundant PROMPT tab (use header copy dropdown instead)
 *              - Fixed save/unsave in Dashboard mode (uses POST /api/ideas/save)
 */

import { getIdeaImageUrl, supportsImageGeneration } from '../../features/generator/features/image-generation.js';
import { getIdeaVideoUrl } from '../../features/generator/features/video-generation.js';
import { getCategoryColors } from '../../features/generator/ui/idea-card.js';
import { openEditModal } from '../../features/generator/features/image-edit.js';
import { renderHistoryButton } from '../../features/generator/features/version-history.js';
import { renderHeadlineWithNav } from '../../features/generator/features/art-director.js';
import { GeneratorState, persistSessionState } from '../../features/generator/core/state.js';
import { Icons } from './icons.js';
import { escapeHtml } from '../core/sanitize.js';
import { onDocument, cleanupScope, subscribe, emit } from '../core/events.js';
import { IDEA_EVENTS, IMAGE_EVENTS, VIDEO_EVENTS, UI_EVENTS } from '../core/event-types.js';
import { apiRequest } from '../api/client.js';

// Storyboard imports
import {
  initStoryboardUI,
  renderStoryboard,
  renderPanelsView,
  renderVideosTab
} from '../../features/generator/storyboard/ui.js';

import {
  setActiveIdeaId,
  loadFromIdea,
  setSplitSettings,
  invalidateStoryboard
} from '../../features/generator/storyboard/state.js';

// Sub-module imports (extracted for modularity)
import {
  switchTab as switchTabModule,
  handleImportScenes as handleImportScenesModule,
  handleGenerateStoryboardImage as handleGenerateStoryboardImageModule,
  handleGenerateScenes as handleGenerateScenesModule,
  handleCopyStoryboardPrompt
} from './idea-canvas/index.js';

// Copy dropdown import - call directly with idea+brief
import { openCopyDropdown, initCopyPromptDropdown } from '../../features/generator/ui/copy-dropdown.js';

// Selection manager imports - for clearing selections when canvas opens
import { ideasSelection, harvestSelection } from '../core/selection-manager.js';

import {
  buildImagePrompt,
  buildStyledPrompt,
  showCanvasGeneratingPlaceholder
} from './idea-canvas/index.js';

import {
  // V2 Refine functions
  initRefineTab,
  renderRefineTabReady,
  renderRefineTabPreview,
  renderConversionPreview,
  handleModeChange as handleRefineModeChange,
  handleRefine as handleRefineV2,
  handleAcceptRefinement,
  handleDiscardRefinement,
  handlePreviewHeadlineNav,
  handleHeadlineSelect,
  handleHeadlineMergeModeChange,
  // Format conversion handlers
  handleSelectConvertFormat,
  handleCancelConvertFormat,
  handleStartConversion,
  handleConvertFormat,
  handleAcceptConversion,
  getRefineState,
  resetRefineState
} from './idea-canvas/index.js';

export const IdeaCanvas = (function() {
  'use strict';

  // ============================================================================
  // STATE
  // ============================================================================

  let gridContainer = null;
  let canvasContainer = null;
  let canvasInner = null;
  let currentIdea = null;
  let savedScrollY = 0;
  let initialized = false;
  let storyboardInitialized = false;
  let context = 'generator'; // 'generator' or 'dashboard'

  // Category colors fallback
  const CategoryColorsFallback = {
    'A': '#FF00FF', 'B': '#FF6600', 'C': '#00FFFF', 'D': '#00FF00',
    'E': '#9900FF', 'F': '#FFFF00', 'G': '#0066FF', 'H': '#FF0000',
    'I': '#FF6B9D', 'J': '#4ECDC4', 'K': '#9B59B6', 'L': '#E74C3C',
    'M': '#2ECC71', 'N': '#1ABC9C', 'O': '#F39C12'
  };

  // ============================================================================
  // EDIT STATE MANAGEMENT (Task 75: Idea Canvas Editing)
  // ============================================================================

  // Edit state - tracks changes to the current idea
  let editState = {
    isEditing: false,
    originalIdea: null,      // Snapshot when edit started
    currentIdea: null,       // Live edited values
    dirtyFields: new Set(),  // Track which fields changed
    isSaving: false
  };

  // Headline manager state - tracks all headlines (original + alternatives)
  let headlineState = {
    currentIndex: 0,
    items: []  // { text: string, isOriginal: boolean }[]
  };

  // REFACTOR-03: Event subscriptions for cross-module communication
  let eventUnsubscribers = [];

  /**
   * REFACTOR-03: Set up event subscriptions for cross-module communication
   * Listens for idea updates, image generation, and video generation events
   * @param {string} ideaId - The current idea ID
   */
  function setupEventSubscriptions(ideaId) {
    // Clear any existing subscriptions
    eventUnsubscribers.forEach(unsub => unsub());
    eventUnsubscribers = [];

    // Subscribe to idea updates for this idea
    eventUnsubscribers.push(
      subscribe(IDEA_EVENTS.UPDATED, ({ idea }) => {
        if (idea && String(idea.id) === String(ideaId) && currentIdea) {
          console.log('[IdeaCanvas] Idea updated via event:', idea.id);
          Object.assign(currentIdea, idea);
          render(currentIdea);
        }
      }, { scope: 'idea-canvas' })
    );

    // Subscribe to image generation completed
    eventUnsubscribers.push(
      subscribe(IMAGE_EVENTS.GENERATION_COMPLETED, ({ ideaId: genIdeaId, imageUrl }) => {
        if (String(genIdeaId) === String(ideaId) && currentIdea) {
          console.log('[IdeaCanvas] Image generated via event:', genIdeaId);
          currentIdea.imageUrl = imageUrl;
          currentIdea.image_url = imageUrl;
          render(currentIdea);
        }
      }, { scope: 'idea-canvas' })
    );

    // Subscribe to video generation completed
    eventUnsubscribers.push(
      subscribe(VIDEO_EVENTS.GENERATION_COMPLETED, ({ ideaId: genIdeaId, videoUrl }) => {
        if (String(genIdeaId) === String(ideaId) && currentIdea) {
          console.log('[IdeaCanvas] Video generated via event:', genIdeaId);
          currentIdea.videoUrl = videoUrl;
          currentIdea.video_url = videoUrl;
          render(currentIdea);
        }
      }, { scope: 'idea-canvas' })
    );

    // Subscribe to idea refined
    eventUnsubscribers.push(
      subscribe(IDEA_EVENTS.REFINED, ({ idea }) => {
        if (idea && String(idea.id) === String(ideaId) && currentIdea) {
          console.log('[IdeaCanvas] Idea refined via event:', idea.id);
          Object.assign(currentIdea, idea);
          render(currentIdea);
        }
      }, { scope: 'idea-canvas' })
    );

    console.log('[IdeaCanvas] Event subscriptions set up for idea:', ideaId);
  }

  /**
   * REFACTOR-03: Clean up event subscriptions
   */
  function cleanupEventSubscriptions() {
    eventUnsubscribers.forEach(unsub => unsub());
    eventUnsubscribers = [];
    console.log('[IdeaCanvas] Event subscriptions cleaned up');
  }

  /**
   * Get the correct headline array key based on format
   * @param {string} format - The idea format
   * @returns {string} The meta key for alternative headlines
   */
  function getAlternativeHeadlinesKey(format) {
    switch (format) {
      case 'video': return 'alternativeSupers';
      case 'social': return 'alternativeHooks';
      case 'digital': return 'alternativeCTAs';
      case 'experiential': return 'alternativeReveals';
      case 'campaign': return 'alternativeLines';
      default: return 'alternativeHeadlines';
    }
  }

  /**
   * Get the correct headline field name based on format
   * @param {string} format - The idea format
   * @returns {string} The field name for the primary headline
   */
  function getHeadlineFieldName(format) {
    switch (format) {
      case 'video': return 'super';
      case 'social': return 'hook';
      case 'digital': return 'cta';
      case 'experiential': return 'reveal';
      case 'campaign': return 'campaignLine';
      default: return 'headline';
    }
  }

  /**
   * Initialize edit state when canvas opens
   * @param {Object} idea - The idea object
   */
  function initEditState(idea) {
    editState.originalIdea = JSON.parse(JSON.stringify(idea));
    editState.currentIdea = JSON.parse(JSON.stringify(idea));
    editState.dirtyFields.clear();
    editState.isEditing = false;
    editState.isSaving = false;

    // Initialize headline state based on format
    const format = (idea.format || 'print').toLowerCase();
    const meta = idea.meta || {};
    const altKey = getAlternativeHeadlinesKey(format);
    const headlineField = getHeadlineFieldName(format);

    // Get primary headline (format-specific or generic)
    const primaryHeadline = idea[headlineField] || meta[headlineField] || idea.headline || '';

    headlineState.currentIndex = idea.currentHeadlineIndex || meta.currentHeadlineIndex || 0;
    headlineState.items = [
      { text: primaryHeadline, isOriginal: true }
    ];

    // Add alternative headlines from format-specific key
    const alternatives = idea[altKey] || meta[altKey] || idea.alternativeHeadlines || [];
    if (alternatives.length > 0) {
      alternatives.forEach(alt => {
        const altText = typeof alt === 'string' ? alt : (alt[headlineField] || alt.headline || alt);
        headlineState.items.push({
          text: altText,
          isOriginal: false
        });
      });
    }

    console.log('[IdeaCanvas] Edit state initialized:', {
      fieldCount: Object.keys(idea).length,
      headlines: headlineState.items.length,
      currentIndex: headlineState.currentIndex
    });

    // Ensure visual state is correct (no unsaved changes indicator)
    updateSaveButtonVisibility();
  }

  /**
   * Get field value from idea object (handles meta fields and aliases)
   * @param {Object} idea - The idea object
   * @param {string} field - Field name
   * @returns {string} Field value
   */
  function getFieldValue(idea, field) {
    const meta = idea.meta || {};
    switch (field) {
      case 'title': return meta.title || idea.title || '';
      case 'idea': return meta.idea || idea.idea || '';
      case 'notes': return meta.notes || '';
      case 'headline': return idea.headline || '';
      case 'subline': return (idea.subline && idea.subline !== 'null') ? idea.subline : '';
      case 'visual': return idea.visual || idea.text || '';
      case 'whyItWorks': return idea.whyItWorks || idea.explanation || idea.why_it_works || '';
      case 'sourceFragment': return idea.sourceFragment || idea.source_fragment || '';
      case 'sourceTruth': return idea.sourceTruth || idea.source_truth || '';
      default: return idea[field] || meta[field] || '';
    }
  }

  /**
   * Set field value on idea object
   * @param {Object} idea - The idea object to update
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  function setFieldValue(idea, field, value) {
    idea.meta = idea.meta || {};
    switch (field) {
      case 'title':
        idea.meta.title = value;
        idea.title = value;
        break;
      case 'idea':
        idea.meta.idea = value;
        idea.idea = value;
        break;
      case 'notes':
        idea.meta.notes = value;
        break;
      case 'headline':
        idea.headline = value;
        break;
      case 'subline':
        idea.subline = value;
        break;
      case 'visual':
        idea.visual = value;
        idea.text = value;
        break;
      case 'whyItWorks':
        idea.whyItWorks = value;
        idea.explanation = value;
        break;
      case 'sourceFragment':
        idea.sourceFragment = value;
        idea.source_fragment = value;
        break;
      case 'sourceTruth':
        idea.sourceTruth = value;
        idea.source_truth = value;
        break;
      default:
        idea[field] = value;
    }
  }

  /**
   * Track a field change
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  function trackChange(field, value) {
    const originalValue = getFieldValue(editState.originalIdea, field);

    // Normalize for comparison (trim, handle null/undefined)
    const normalizedOriginal = (originalValue || '').toString().trim();
    const normalizedNew = (value || '').toString().trim();

    if (normalizedNew !== normalizedOriginal) {
      editState.dirtyFields.add(field);
    } else {
      editState.dirtyFields.delete(field);
    }

    setFieldValue(editState.currentIdea, field, value);
    updateSaveButtonVisibility();
  }

  /**
   * Check if there are unsaved changes
   * @returns {boolean}
   */
  function isDirty() {
    return editState.dirtyFields.size > 0;
  }

  /**
   * Mark headlines as dirty (for headline manager changes)
   */
  function markHeadlinesDirty() {
    editState.dirtyFields.add('headlines');
    updateSaveButtonVisibility();
  }

  /**
   * Show/hide save button and update visual indicators based on dirty state
   */
  function updateSaveButtonVisibility() {
    const saveBtn = canvasInner?.querySelector('.canvas-save-changes-btn');
    const dirty = isDirty();

    // Update save button
    if (saveBtn) {
      if (dirty) {
        saveBtn.style.display = 'inline-flex';
        saveBtn.disabled = false;
      } else {
        saveBtn.style.display = 'none';
      }
    }

    // Update canvas border indicator
    if (canvasInner) {
      canvasInner.classList.toggle('has-unsaved-changes', dirty);
    }

    // Update notes section indicator if it has content
    const notesSection = canvasInner?.querySelector('.canvas-notes-section');
    const notesInput = canvasInner?.querySelector('.canvas-notes-input');
    if (notesSection && notesInput) {
      const hasContent = notesInput.value.trim().length > 0;
      notesSection.classList.toggle('has-content', hasContent);
    }
  }

  /**
   * Save all changes to backend
   */
  async function saveChanges() {
    if (!isDirty() || editState.isSaving || !currentIdea) return;

    editState.isSaving = true;
    const saveBtn = canvasInner?.querySelector('.canvas-save-changes-btn');
    if (saveBtn) {
      const btnText = saveBtn.querySelector('.btn-text');
      if (btnText) btnText.textContent = 'SAVING...';
      saveBtn.disabled = true;
      saveBtn.classList.add('is-loading');
    }

    try {
      // Build update payload
      const updates = {};
      const format = editState.currentIdea.format || 'print';
      const altKey = getAlternativeHeadlinesKey(format);

      editState.dirtyFields.forEach(field => {
        if (field === 'headlines') {
          // Handle headline manager changes
          updates.headline = headlineState.items[0]?.text || '';

          // Save alternatives as objects with headline/subline structure (matches generation format)
          const alternativesAsObjects = headlineState.items.slice(1).map(h => ({
            headline: h.text,
            subline: null
          }));
          updates.alternativeHeadlines = alternativesAsObjects;
          updates.currentHeadlineIndex = headlineState.currentIndex;

          // Also update format-specific alternative key in meta
          if (altKey !== 'alternativeHeadlines') {
            updates.meta = updates.meta || {};
            updates.meta[altKey] = alternativesAsObjects;
          }
        } else {
          const value = getFieldValue(editState.currentIdea, field);
          // Map to API field names
          switch (field) {
            case 'title':
            case 'idea':
            case 'notes':
              updates[field] = value;
              break;
            case 'headline':
            case 'visual':
            case 'subline':
            case 'whyItWorks':
            case 'sourceFragment':
            case 'sourceTruth':
              updates[field] = value;
              break;
            default:
              updates[field] = value;
          }
        }
      });

      // FIX: When visual field is manually edited for format-specific types,
      // clear individual meta fields so the visual text blob is used going forward
      if (editState.dirtyFields.has('visual')) {
        const formatSpecificFields = {
          social: ['hook', 'socialFormat', 'sequence', 'patternInterrupt', 'sound', 'caption'],
          digital: ['sizes', 'hook', 'frames', 'interaction', 'cta'],
          experiential: ['scale', 'location', 'setup', 'participation', 'reveal', 'capture', 'takeaway'],
          campaign: ['campaignLine', 'manifesto', 'heroExecution', 'systemLogic', 'extensions', 'participation', 'earned']
        };

        const fieldsToNullify = formatSpecificFields[format];
        if (fieldsToNullify) {
          updates.meta = updates.meta || {};
          fieldsToNullify.forEach(f => {
            updates.meta[f] = null;
          });
          console.log('[IdeaCanvas] Clearing format-specific fields for', format, ':', fieldsToNullify);
        }
      }

      console.log('[IdeaCanvas] Saving changes:', updates);

      const response = await apiRequest(`/api/ideas/${currentIdea.id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });

      // Parse response JSON (apiRequest returns a Response object, not parsed data)
      const data = await response.json();

      if (data.success) {
        // If we cleared format-specific fields, also clear them in local state
        if (updates.meta) {
          editState.currentIdea.meta = editState.currentIdea.meta || {};
          Object.keys(updates.meta).forEach(key => {
            if (updates.meta[key] === null) {
              editState.currentIdea.meta[key] = null;
              editState.currentIdea[key] = null; // Also clear top-level
            }
          });
        }

        // Update local state
        editState.originalIdea = JSON.parse(JSON.stringify(editState.currentIdea));
        editState.dirtyFields.clear();

        // Update currentIdea reference
        Object.assign(currentIdea, editState.currentIdea);

        // Update global Ideas.items for search index sync (critical fix #3)
        if (window.Ideas?.items) {
          const idx = window.Ideas.items.findIndex(i => String(i.id) === String(currentIdea.id));
          if (idx !== -1) {
            Object.assign(window.Ideas.items[idx], editState.currentIdea);
            console.log('[IdeaCanvas] Updated Ideas.items for search sync');
          }
        }

        // Update the idea card in the dashboard grid (if visible) without full page reload
        if (window.IdeasModule?.updateIdeaCard) {
          window.IdeasModule.updateIdeaCard(currentIdea);
        }

        if (window.Toast) window.Toast.success('Changes saved');
        if (window.Sounds) window.Sounds.play('success');
      } else {
        throw new Error(data.error || 'Save failed');
      }
    } catch (err) {
      console.error('[IdeaCanvas] Save failed:', err);
      if (window.Toast) window.Toast.error('Failed to save changes');
      if (window.Sounds) window.Sounds.play('error');
    } finally {
      editState.isSaving = false;
      if (saveBtn) {
        const btnText = saveBtn.querySelector('.btn-text');
        if (btnText) btnText.textContent = 'SAVE';
        saveBtn.disabled = false;  // Always re-enable after save attempt
        saveBtn.classList.remove('is-loading');
      }
      updateSaveButtonVisibility();
    }
  }

  /**
   * Discard all changes and revert to original
   */
  function discardChanges() {
    if (!isDirty()) return;

    editState.currentIdea = JSON.parse(JSON.stringify(editState.originalIdea));
    editState.dirtyFields.clear();

    // Re-initialize headline state
    initEditState(editState.originalIdea);

    // Re-render with original values
    render(editState.originalIdea);
    initializeCanvasScripts(editState.originalIdea);

    updateSaveButtonVisibility();
    console.log('[IdeaCanvas] Changes discarded');
  }

  // ============================================================================
  // EDIT FIELD HANDLERS (Task 75)
  // ============================================================================

  /**
   * Handle click on editable field - transform to input
   * @param {HTMLElement} editable - The editable element
   */
  function handleEditableClick(editable) {
    const field = editable.dataset.field;
    const isMultiline = editable.dataset.multiline === 'true';

    // For visual field, use the displayed text (which may be formatted from individual fields)
    // For other fields, get from idea object
    let currentValue;
    if (field === 'visual') {
      // Get the displayed text content (not the raw idea.visual which may be different)
      currentValue = editable.textContent.trim();
      // Don't use placeholder text
      if (currentValue === 'Click to add visual description...') {
        currentValue = '';
      }
    } else {
      currentValue = getFieldValue(editState.currentIdea, field);
    }

    // Capture original height before modifying
    const originalHeight = editable.offsetHeight;

    // Create input element
    let input;
    if (isMultiline) {
      input = document.createElement('textarea');
      input.className = 'canvas-edit-textarea';
      // Set initial height to match displayed content (notepad-style, no jumping)
      input.style.height = Math.max(originalHeight, 60) + 'px';
      input.style.minHeight = '60px';
    } else {
      input = document.createElement('input');
      input.type = 'text';
      input.className = 'canvas-edit-input';
    }

    input.value = currentValue;
    input.dataset.field = field;

    // Store original content for cancel
    editable.dataset.originalContent = editable.innerHTML;
    editable.classList.add('editing');
    editable.innerHTML = '';
    editable.appendChild(input);

    // Auto-resize textarea as user types (notepad-style)
    if (isMultiline) {
      const autoResize = () => {
        input.style.height = 'auto';
        input.style.height = Math.max(input.scrollHeight, 60) + 'px';
      };
      input.addEventListener('input', autoResize);
      // Initial resize to fit content
      autoResize();
    }

    input.focus();
    // Don't select all - place cursor at end for natural typing
    input.selectionStart = input.selectionEnd = input.value.length;

    // Handle blur - save to state
    input.addEventListener('blur', () => {
      handleEditableBlur(editable, input);
    });

    // Handle keydown
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Cancel edit
        editable.innerHTML = editable.dataset.originalContent;
        editable.classList.remove('editing');
        delete editable.dataset.originalContent;
      } else if (e.key === 'Enter' && !isMultiline) {
        // Save on Enter for single-line
        input.blur();
      }
    });
  }

  /**
   * Handle blur on edit field - save to local state
   * @param {HTMLElement} editable - The editable container
   * @param {HTMLInputElement|HTMLTextAreaElement} input - The input element
   */
  function handleEditableBlur(editable, input) {
    const field = input.dataset.field;
    const newValue = input.value.trim();

    // Update local state
    trackChange(field, newValue);

    // Re-render the field in view mode
    editable.classList.remove('editing');
    delete editable.dataset.originalContent;

    // Render content appropriately
    if (newValue) {
      editable.innerHTML = escapeHtml(newValue);
    } else {
      editable.innerHTML = '<span class="empty-placeholder">Click to add...</span>';
    }
  }

  // ============================================================================
  // HEADLINE MANAGER HANDLERS (Task 75)
  // ============================================================================

  /**
   * Add a new headline to the list
   * @param {string} text - The headline text
   */
  function handleAddHeadline(text) {
    if (!text.trim()) return;

    headlineState.items.push({ text: text.trim(), isOriginal: false });
    markHeadlinesDirty();

    // Re-render headline manager
    const manager = canvasInner?.querySelector('.headline-manager');
    if (manager && currentIdea) {
      const format = currentIdea.format || 'print';
      const headlineLabel = getHeadlineLabelForFormat(format);
      manager.outerHTML = renderHeadlineManager(currentIdea, String(currentIdea.id), headlineLabel);

      // Re-attach event listeners for the new input
      const newInput = canvasInner?.querySelector('.headline-add-input');
      if (newInput) {
        attachHeadlineInputListener(newInput);
      }
    }

    console.log('[IdeaCanvas] Added headline, total:', headlineState.items.length);
  }

  /**
   * Delete a headline from the list
   * @param {number} index - The index to delete
   */
  function handleDeleteHeadline(index) {
    if (headlineState.items.length <= 1) {
      if (window.Toast) window.Toast.error('Must keep at least one headline');
      return;
    }

    headlineState.items.splice(index, 1);

    // Adjust current index if needed
    if (headlineState.currentIndex >= index) {
      headlineState.currentIndex = Math.max(0, headlineState.currentIndex - 1);
    }

    markHeadlinesDirty();

    // Re-render headline manager
    const manager = canvasInner?.querySelector('.headline-manager');
    if (manager && currentIdea) {
      const format = currentIdea.format || 'print';
      const headlineLabel = getHeadlineLabelForFormat(format);
      manager.outerHTML = renderHeadlineManager(currentIdea, String(currentIdea.id), headlineLabel);

      // Re-attach event listeners for the input
      const newInput = canvasInner?.querySelector('.headline-add-input');
      if (newInput) {
        attachHeadlineInputListener(newInput);
      }
    }

    console.log('[IdeaCanvas] Deleted headline at index:', index);
  }

  /**
   * Set a headline as the current/active one
   * @param {number} index - The index to set as current
   */
  function handleSetCurrentHeadline(index) {
    if (index < 0 || index >= headlineState.items.length) return;

    headlineState.currentIndex = index;
    markHeadlinesDirty();

    // Update UI
    const items = canvasInner?.querySelectorAll('.headline-item');
    items?.forEach((item, i) => {
      item.classList.toggle('is-current', i === index);
      const indicator = item.querySelector('.headline-current-indicator');
      if (indicator) {
        indicator.textContent = i === index ? '★' : '☆';
        indicator.title = i === index ? 'Current headline' : 'Set as current';
      }
    });

    console.log('[IdeaCanvas] Set current headline:', index);
  }

  /**
   * Update headline text inline
   * @param {number} index - The headline index
   * @param {string} newText - The new text
   */
  function handleUpdateHeadlineText(index, newText) {
    if (index < 0 || index >= headlineState.items.length) return;

    headlineState.items[index].text = newText;
    markHeadlinesDirty();
  }

  /**
   * Get headline label for format
   * @param {string} format - The idea format
   * @returns {string} The label to display
   */
  function getHeadlineLabelForFormat(format) {
    switch (format) {
      case 'video': return 'SUPER';
      case 'social': return 'HOOK';
      case 'digital': return 'CTA';
      case 'experiential': return 'REVEAL';
      case 'campaign': return 'CAMPAIGN LINE';
      default: return 'HEADLINE';
    }
  }

  /**
   * Attach event listeners to headline add input
   * @param {HTMLInputElement} input - The input element
   */
  function attachHeadlineInputListener(input) {
    if (input._listenerAttached) return;
    input._listenerAttached = true;

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddHeadline(input.value);
        input.value = '';
      }
    });
  }

  /**
   * Render the headline manager component
   * @param {Object} idea - The idea object
   * @param {string} ideaId - The idea ID
   * @param {string} headlineLabel - The label for headlines
   * @returns {string} HTML string
   */
  function renderHeadlineManager(idea, ideaId, headlineLabel) {
    const headlines = headlineState.items;
    const currentIdx = headlineState.currentIndex;

    return `
      <div class="headline-manager" data-idea-id="${ideaId}">
        <div class="headline-manager-header">
          <span class="canvas-block-label">${headlineLabel}S</span>
          <span class="headline-count">(${headlines.length})</span>
        </div>

        <ul class="headline-list">
          ${headlines.map((h, i) => `
            <li class="headline-item ${i === currentIdx ? 'is-current' : ''}"
                data-index="${i}">
              <span class="headline-current-indicator"
                    title="${i === currentIdx ? 'Current headline' : 'Set as current'}">
                ${i === currentIdx ? '★' : '☆'}
              </span>
              <span class="headline-text"
                    contenteditable="true"
                    data-index="${i}">"${escapeHtml(h.text)}"</span>
              <button class="headline-delete"
                      data-index="${i}"
                      title="Delete headline"
                      ${headlines.length <= 1 ? 'disabled' : ''}>×</button>
            </li>
          `).join('')}
        </ul>

        <div class="headline-add-row">
          <input type="text"
                 class="headline-add-input"
                 placeholder="Type new headline...">
          <button class="headline-add-btn" type="button">ADD</button>
        </div>
      </div>
    `;
  }

  /**
   * Render the notes section component
   * @param {Object} idea - The idea object
   * @returns {string} HTML string
   */
  function renderNotesSection(idea) {
    const notes = idea.meta?.notes || '';
    const hasContent = notes.trim().length > 0;

    return `
      <div class="canvas-notes-section ${hasContent ? 'has-content' : ''}">
        <div class="canvas-notes-header">
          <svg class="notes-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <!-- Pixel pen icon -->
            <rect x="2" y="12" width="2" height="2"/>
            <rect x="4" y="10" width="2" height="2"/>
            <rect x="6" y="8" width="2" height="2"/>
            <rect x="8" y="6" width="2" height="2"/>
            <rect x="10" y="4" width="2" height="2"/>
            <rect x="12" y="2" width="2" height="2"/>
            <rect x="10" y="2" width="2" height="2"/>
            <rect x="12" y="4" width="2" height="2"/>
            <rect x="0" y="14" width="2" height="2"/>
          </svg>
          <span class="canvas-block-label">NOTES</span>
        </div>
        <textarea
          class="canvas-notes-input"
          placeholder="Add your notes here..."
        >${escapeHtml(notes)}</textarea>
      </div>
    `;
  }

  // Tab icons - custom pixel art icons (PROMPT tab removed - use header copy dropdown instead)
  const TAB_ICONS = {
    idea: '/assets/icons/canvas-detail-icon_0000_idea.png',
    refine: '/assets/icons/canvas-detail-icon_0008_refine.png',
    storyboard: '/assets/icons/canvas-detail-icon_0002_storyboard.png',
    panels: '/assets/icons/canvas-detail-icon_0005_panels.png',
    scenes: '/assets/icons/canvas-detail-icon_0004_scenes.png',
    videos: '/assets/icons/canvas-detail-icon_0006_video.png'
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Check if format supports video workflow tabs
   */
  function supportsVideoWorkflow(format) {
    return format?.toLowerCase() === 'video';
  }

  /**
   * Set the idea context for modules that need it
   */
  function setIdeaContext(idea) {
    if (window.GeneratorState) {
      const existing = window.GeneratorState.generatedIdeas.flatMap(g => g.ideas).find(i => i.id === idea.id);
      if (!existing) {
        let tempGroup = window.GeneratorState.generatedIdeas.find(g => g.trigger === '_canvas');
        if (!tempGroup) {
          tempGroup = { trigger: '_canvas', ideas: [] };
          window.GeneratorState.generatedIdeas.push(tempGroup);
        }
        if (!tempGroup.ideas.find(i => i.id === idea.id)) {
          tempGroup.ideas.push(idea);
        }
      }
    }
  }

  // ============================================================================
  // TAB SWITCHING (Wrapper for sub-module)
  // ============================================================================

  function switchTab(tabName) {
    switchTabModule(canvasContainer, currentIdea, tabName, {
      populateStoryboardPrompt,
      // V2: Use initRefineTab which fetches available modes and renders the v2 UI
      populateRefineTab: (idea) => {
        initRefineTab(idea).catch(err => {
          console.error('[IdeaCanvas] Failed to initialize refine tab:', err);
        });
      }
    });
  }

  // ============================================================================
  // PROMPT BUILDERS
  // ============================================================================

  /**
   * Build storyboard prompt from idea
   */
  function buildStoryboardPrompt(idea) {
    const meta = idea.meta || {};
    let visual = idea.visual || idea.text || '';
    const format = (idea.format || 'print').toLowerCase();

    // For video ideas, build visual from scenes if top-level visual is empty
    if (!visual && format === 'video') {
      const scenes = idea.scenes || meta.scenes || meta.storyboard?.scenes || idea.structure?.scenes || [];
      if (scenes.length > 0) {
        visual = scenes.map(s => `Scene ${s.seq}: ${s.visual}`).join('\n');
      }
    }

    const headline = idea.headline || (format === 'video' ? (idea.super || meta.super || '') : '');

    const panelCount = document.getElementById('canvas-storyboard-panel-count')?.value || '6';
    const aspect = document.getElementById('canvas-storyboard-aspect')?.value || '1:1';

    const panelConfigs = {
      '4': { rows: 2, cols: 2 },
      '6': { rows: 3, cols: 2 },
      '9': { rows: 3, cols: 3 },
      '12': { rows: 4, cols: 3 }
    };
    const config = panelConfigs[panelCount] || { rows: 3, cols: 2 };

    return `Create a ${config.rows}x${config.cols} panel storyboard grid (${panelCount} panels) for this ${format} ad concept:

${visual}

${headline ? `Headline: "${headline}"` : ''}

Requirements:
- Single cohesive image with exactly ${panelCount} panels in a ${config.rows}x${config.cols} grid
- Each panel is a distinct scene showing story progression
- Consistent visual style across all panels
- ${aspect} aspect ratio for the overall grid
- Clear panel borders for easy splitting
- Cinematic commercial quality`;
  }

  function populateStoryboardPrompt(idea) {
    const textarea = document.getElementById('canvas-storyboard-textarea');
    if (textarea) {
      textarea.value = buildStoryboardPrompt(idea);
    }
  }

  // NOTE: buildFullPrompt and populateFullPrompt removed - PROMPT tab is gone
  // Use the copy dropdown in header instead (calls openCopyDropdown with idea+brief)

  // ============================================================================
  // EVENT HANDLERS (Wrappers for sub-modules)
  // ============================================================================

  function handleImportScenes() {
    handleImportScenesModule(currentIdea);
  }

  function handleGenerateScenes() {
    if (!currentIdea) return;
    handleGenerateScenesModule(currentIdea, (idea) => { currentIdea = idea; });
  }

  function handleGenerateStoryboardImage() {
    handleGenerateStoryboardImageModule(currentIdea);
  }

  // ============================================================================
  // SAVE/DELETE HANDLERS
  // ============================================================================

  async function toggleSave(ideaId, button) {
    if (context === 'generator') {
      if (window.GeneratorState) {
        const savedIdeas = window.GeneratorState.savedIdeas;
        const index = savedIdeas.indexOf(ideaId);
        if (index === -1) {
          savedIdeas.push(ideaId);
          button.classList.add('saved');
          button.title = 'Unsave';
        } else {
          savedIdeas.splice(index, 1);
          button.classList.remove('saved');
          button.title = 'Save';
        }
        const savedCount = document.getElementById('saved-count');
        if (savedCount) {
          savedCount.textContent = savedIdeas.length;
        }
      }
    } else {
      // Dashboard mode - use the API with auth
      try {
        const isSaved = button.classList.contains('saved');
        const response = await apiRequest('/api/ideas/save', {
          method: 'POST',
          body: JSON.stringify({ ideaId, saved: !isSaved })
        });
        if (response.ok) {
          const newSavedState = !isSaved;
          button.classList.toggle('saved');
          button.title = newSavedState ? 'Unsave' : 'Save';

          // Update canvas current idea
          if (currentIdea) {
            currentIdea.saved = newSavedState;
            currentIdea.is_saved = newSavedState;
          }

          // Sync back to Ideas.items so grid view stays current
          if (window.Ideas?.items) {
            const idx = window.Ideas.items.findIndex(i => String(i.id) === String(ideaId));
            if (idx !== -1) {
              window.Ideas.items[idx].saved = newSavedState;
              window.Ideas.items[idx].is_saved = newSavedState;
              console.log('[IdeaCanvas] Synced save state to Ideas.items');
            }
          }

          // Update the card in the grid if visible
          const cardBtn = document.querySelector(`.idea-card[data-id="${ideaId}"] .idea-save-btn`);
          if (cardBtn) {
            cardBtn.classList.toggle('saved', newSavedState);
            cardBtn.title = newSavedState ? 'Unsave' : 'Save';
          }

          // Dispatch event for other listeners (e.g., filter counts)
          window.dispatchEvent(new CustomEvent('idea:saved', {
            detail: { ideaId, saved: newSavedState }
          }));

          // Show feedback
          if (window.Toast) {
            window.Toast.success(newSavedState ? 'Idea saved' : 'Idea unsaved');
          }
        }
      } catch (err) {
        console.error('[IdeaCanvas] Failed to toggle save:', err);
        if (window.Toast) window.Toast.error('Failed to save idea');
      }
    }
  }

  async function confirmDelete(ideaId) {
    if (window.Modal && window.Modal.confirm) {
      window.Modal.confirm({
        title: 'Delete Idea',
        message: 'Are you sure you want to delete this idea? This cannot be undone.',
        confirmText: 'Delete',
        confirmClass: 'btn-danger',
        onConfirm: async () => {
          await executeDelete(ideaId);
        }
      });
    } else {
      if (confirm('Are you sure you want to delete this idea? This cannot be undone.')) {
        await executeDelete(ideaId);
      }
    }
  }

  async function executeDelete(ideaId) {
    try {
      const response = await apiRequest(`/api/ideas/${ideaId}`, { method: 'DELETE' });

      if (response.ok) {
        if (window.Toast) window.Toast.success('Idea deleted');
        if (window.Sounds) window.Sounds.play('success');
        close();
        if (window.IdeasPage && window.IdeasPage.reload) {
          window.IdeasPage.reload();
        } else {
          window.location.reload();
        }
      } else {
        throw new Error('Delete failed');
      }
    } catch (err) {
      console.error('[IdeaCanvas] Delete failed:', err);
      if (window.Toast) window.Toast.error('Failed to delete idea');
      if (window.Sounds) window.Sounds.play('error');
    }
  }

  // ============================================================================
  // HEADLINE NAVIGATION
  // ============================================================================

  /**
   * Handle headline navigation (cycle through alternative headlines)
   * @param {number} direction - 1 for next, -1 for previous
   */
  function handleHeadlineNav(direction) {
    if (!currentIdea) return;

    const format = currentIdea.format || 'print';
    const meta = currentIdea.meta || {};

    // Get format-specific alternatives
    let alternatives = currentIdea.alternativeHeadlines || [];
    let headlineFieldName = 'headline';  // Field name for the headline (varies by format)

    if (format === 'video') {
      alternatives = currentIdea.alternativeSupers || meta.alternativeSupers || [];
      headlineFieldName = 'super';
    } else if (format === 'social') {
      alternatives = currentIdea.alternativeHooks || meta.alternativeHooks || [];
      headlineFieldName = 'hook';
    } else if (format === 'digital') {
      alternatives = currentIdea.alternativeCTAs || meta.alternativeCTAs || [];
      headlineFieldName = 'cta';
    } else if (format === 'experiential') {
      alternatives = currentIdea.alternativeReveals || meta.alternativeReveals || [];
      headlineFieldName = 'reveal';
    } else if (format === 'campaign') {
      alternatives = currentIdea.alternativeLines || meta.alternativeLines || [];
      headlineFieldName = 'campaignLine';
    }

    if (alternatives.length === 0) return;

    const currentIndex = currentIdea.currentHeadlineIndex || 0;
    const totalHeadlines = alternatives.length + 1; // +1 for original

    // Calculate new index with wrapping
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = totalHeadlines - 1;
    if (newIndex >= totalHeadlines) newIndex = 0;

    // Update the idea object
    currentIdea.currentHeadlineIndex = newIndex;

    // Get headline/subline to display
    let displayHeadline, displaySubline;
    if (newIndex === 0) {
      // Original headline (stored in the idea object when we first opened)
      // Use format-specific field or fallback to headline
      displayHeadline = currentIdea._originalHeadline ||
        currentIdea[headlineFieldName] || meta[headlineFieldName] || currentIdea.headline;
      const origSubline = currentIdea._originalSubline || currentIdea.subline || '';
      displaySubline = (origSubline && origSubline !== 'null') ? origSubline : '';
    } else {
      const alt = alternatives[newIndex - 1];
      // Alternatives can have different field names based on format
      displayHeadline = alt?.[headlineFieldName] || alt?.headline || alt?.super || alt?.hook || alt?.cta || alt?.reveal || alt?.campaignLine || alt || '';
      const altSubline = alt?.subline || '';
      displaySubline = (altSubline && altSubline !== 'null') ? altSubline : '';
    }

    // Update DOM
    const headlineEl = canvasInner.querySelector('.canvas-block-headline');
    const sublineEl = canvasInner.querySelector('.canvas-block-subline');
    const counterEl = canvasInner.querySelector('.canvas-headline-counter');

    if (headlineEl) {
      headlineEl.textContent = `"${displayHeadline}"`;
    }
    if (sublineEl) {
      if (displaySubline && displaySubline !== 'null') {
        sublineEl.textContent = displaySubline;
        sublineEl.style.display = '';
      } else {
        sublineEl.style.display = 'none';
      }
    }
    if (counterEl) {
      counterEl.textContent = `${newIndex + 1}/${totalHeadlines}`;
    }

    console.log(`[IdeaCanvas] Headline ${newIndex + 1}/${totalHeadlines}: "${displayHeadline}"`);
  }

  // ============================================================================
  // CLICK HANDLER (Event Delegation)
  // ============================================================================

  async function handleCanvasClick(e) {
    // Back button
    if (e.target.closest('.btn-canvas-back')) {
      close();
      return;
    }

    // =========================================================================
    // TASK 75: EDIT MODE HANDLERS
    // =========================================================================

    // Save changes button
    const saveChangesBtn = e.target.closest('.canvas-save-changes-btn');
    if (saveChangesBtn) {
      e.stopPropagation();
      saveChanges();
      return;
    }

    // Editable field click - transform to input
    const editable = e.target.closest('[data-editable="true"]');
    if (editable && !editable.classList.contains('editing')) {
      e.stopPropagation();
      handleEditableClick(editable);
      return;
    }

    // Headline manager: Add new headline (Enter in input)
    const headlineAddInput = e.target.closest('.headline-add-input');
    if (headlineAddInput) {
      // Don't stopPropagation - let the input work normally
      return;
    }

    // Headline manager: Set current headline (star click)
    const currentIndicator = e.target.closest('.headline-current-indicator');
    if (currentIndicator) {
      e.stopPropagation();
      const item = currentIndicator.closest('.headline-item');
      if (item) {
        const index = parseInt(item.dataset.index, 10);
        if (!isNaN(index)) {
          handleSetCurrentHeadline(index);
        }
      }
      return;
    }

    // Headline manager: Delete headline
    const headlineDelete = e.target.closest('.headline-delete');
    if (headlineDelete) {
      e.stopPropagation();
      const index = parseInt(headlineDelete.dataset.index, 10);
      if (!isNaN(index)) {
        handleDeleteHeadline(index);
      }
      return;
    }

    // Headline manager: ADD button click
    const headlineAddBtn = e.target.closest('.headline-add-btn');
    if (headlineAddBtn) {
      e.stopPropagation();
      const input = canvasInner?.querySelector('.headline-add-input');
      if (input && input.value.trim()) {
        handleAddHeadline(input.value);
        input.value = '';
        input.focus();
      }
      return;
    }

    // Copy button - call openCopyDropdown directly with idea and brief
    const copyBtn = e.target.closest('.idea-copy-btn');
    if (copyBtn && currentIdea) {
      e.stopPropagation();
      setIdeaContext(currentIdea);
      // Extract brief from idea.meta.brief or fallback to GeneratorState.brief
      const brief = currentIdea.meta?.brief || GeneratorState?.brief || { product: '', benefit: '' };
      openCopyDropdown(String(currentIdea.id), currentIdea, brief);
      return;
    }

    // Save button
    const saveBtn = e.target.closest('.idea-save-btn');
    if (saveBtn) {
      e.stopPropagation();
      const ideaId = saveBtn.dataset.id;
      if (ideaId) toggleSave(ideaId, saveBtn);
      return;
    }

    // Delete button
    const deleteBtn = e.target.closest('.idea-delete-btn');
    if (deleteBtn && context === 'dashboard') {
      e.stopPropagation();
      const ideaId = deleteBtn.dataset.id;
      if (ideaId) confirmDelete(ideaId);
      return;
    }

    // Edit image button
    const editBtn = e.target.closest('.btn-edit');
    if (editBtn) {
      e.stopPropagation();
      const ideaId = editBtn.dataset.id;
      if (ideaId && currentIdea) {
        setIdeaContext(currentIdea);
        openEditModal(ideaId);
      }
      return;
    }

    // + button to generate image (opens modal)
    const addImageBtn = e.target.closest('.btn-canvas-add-image');
    if (addImageBtn) {
      e.stopPropagation();
      if (currentIdea) {
        // CRITICAL: Inject idea into GeneratorState so the modal can find it
        setIdeaContext(currentIdea);
        import('../../features/generator/features/image-generation.js').then(module => {
          module.openImageGenModal(String(currentIdea.id));
        });
      }
      return;
    }

    // Small + button in image/video overlay (opens modal)
    const regenImgBtn = e.target.closest('.btn-regenerate-img');
    if (regenImgBtn && currentIdea) {
      e.stopPropagation();
      setIdeaContext(currentIdea);
      import('../../features/generator/features/image-generation.js').then(module => {
        module.openImageGenModal(String(currentIdea.id));
      });
      return;
    }

    // Big "Generate Image" button
    const generateImageBtn = e.target.closest('.btn-generate-image');
    if (generateImageBtn && currentIdea) {
      e.stopPropagation();
      setIdeaContext(currentIdea);
      import('../../features/generator/features/image-generation.js').then(module => {
        module.openImageGenModal(String(currentIdea.id));
      });
      return;
    }

    // Big "Edit Image" button
    const editImageBtn = e.target.closest('.btn-edit-image');
    if (editImageBtn && currentIdea) {
      e.stopPropagation();
      const ideaId = editImageBtn.dataset.id;
      if (ideaId) {
        setIdeaContext(currentIdea);
        openEditModal(ideaId);
      }
      return;
    }

    // Big "Make Video" button
    const createVideoBtn = e.target.closest('.btn-create-video');
    if (createVideoBtn && currentIdea) {
      e.stopPropagation();
      const ideaId = createVideoBtn.dataset.id;
      if (ideaId) {
        import('../../features/generator/features/video-generation.js').then(module => {
          module.openVideoGenModal(ideaId);
        });
      }
      return;
    }

    // Tab switching (card grid design)
    const tabCard = e.target.closest('.canvas-tab-card');
    if (tabCard) {
      e.stopPropagation();
      switchTab(tabCard.dataset.tab);
      return;
    }

    // Headline navigation (alternative headlines)
    const headlineNavBtn = e.target.closest('.canvas-headline-nav-btn');
    if (headlineNavBtn && currentIdea) {
      e.stopPropagation();
      const direction = headlineNavBtn.classList.contains('canvas-headline-prev') ? -1 : 1;
      handleHeadlineNav(direction);
      return;
    }

    // Storyboard tab buttons
    if (e.target.closest('#btn-canvas-generate-scenes')) {
      e.stopPropagation();
      handleGenerateScenes();
      return;
    }

    if (e.target.closest('#btn-canvas-import-scenes')) {
      e.stopPropagation();
      handleImportScenes();
      return;
    }

    if (e.target.closest('#btn-canvas-gen-storyboard')) {
      e.stopPropagation();
      handleGenerateStoryboardImage();
      return;
    }

    // Copy storyboard prompt button (PROMPT tab removed - only storyboard remains)
    if (e.target.closest('.btn-copy-inline[data-target="canvas-storyboard-textarea"]')) {
      e.stopPropagation();
      handleCopyStoryboardPrompt();
      return;
    }

    // =========================================================================
    // REFINE TAB V2 HANDLERS
    // =========================================================================

    // Refine mode selection (v2) - supports both old and new card design
    const modeOption = e.target.closest('.refine-mode-option') || e.target.closest('.refine-mode-card');
    if (modeOption) {
      e.stopPropagation();
      const radioInput = modeOption.querySelector('input[type="radio"]');
      if (radioInput) {
        const mode = radioInput.value;
        handleRefineModeChange(mode);
      }
      return;
    }

    // Refine idea button (v2 - generates preview)
    if (e.target.closest('#canvas-btn-refine-idea')) {
      e.stopPropagation();
      // Check if v2 mode is active (has mode selection UI)
      let state = getRefineState();
      console.log('[IdeaCanvas] Refine button clicked, state:', state, 'currentIdea:', currentIdea?.title);

      // If currentMode not set, initialize V2 first (user clicked refine before visiting refine tab)
      if (!state.currentMode && currentIdea) {
        console.log('[IdeaCanvas] Initializing V2 refine state before proceeding');
        await initRefineTab(currentIdea);
        state = getRefineState();
      }

      // TASK 75 FIX: Use work-in-progress idea from editState if there are unsaved edits
      // This ensures the AI sees manual edits even if the user hasn't clicked SAVE yet
      const ideaToRefine = isDirty() ? editState.currentIdea : currentIdea;
      console.log('[IdeaCanvas] Using', isDirty() ? 'editState (unsaved edits)' : 'currentIdea', 'for refinement');

      // Always use V2 flow now
      handleRefineV2(ideaToRefine, {
        onPreview: (previewData) => {
          console.log('[IdeaCanvas] Refine preview ready:', previewData.expertLabel);
          // Render preview UI
          const refineContainer = document.querySelector('.canvas-tab-content[data-tab="refine"]');
          if (refineContainer) {
            refineContainer.innerHTML = renderRefineTabPreview(currentIdea, previewData);
          }
        },
        onError: (err) => {
          console.error('[IdeaCanvas] Refine failed:', err);
          if (window.Toast) window.Toast.error('Refinement failed');
        }
      });
      return;
    }

    // Accept refinement button (v2)
    if (e.target.closest('#preview-btn-accept')) {
      e.stopPropagation();
      const generateImage = document.getElementById('preview-generate-image')?.checked ?? false;

      // Show generating placeholder if image will be generated
      if (generateImage && canvasInner) {
        showCanvasGeneratingPlaceholder(canvasInner, 'image');
      }

      handleAcceptRefinement(currentIdea, {
        onComplete: (refinedData, imageData) => {
          console.log('[IdeaCanvas] Refinement complete, imageData:', imageData, 'refinedData.imageUrl:', refinedData?.imageUrl);

          // Update the current idea with refined data
          Object.assign(currentIdea, refinedData);

          // Handle image URL - check multiple possible locations
          const newImageUrl = imageData?.url || refinedData?.imageUrl || refinedData?.image_url;
          if (newImageUrl) {
            currentIdea.imageUrl = newImageUrl;
            // Also update preloaded images cache
            if (!GeneratorState.preloadedImages) {
              GeneratorState.preloadedImages = {};
            }
            GeneratorState.preloadedImages[String(currentIdea.id)] = newImageUrl;
            console.log('[IdeaCanvas] Updated image URL:', newImageUrl);

            // Task 102: Update latestMediaTimestamp for display priority
            if (!GeneratorState.latestMediaTimestamp) GeneratorState.latestMediaTimestamp = {};
            GeneratorState.latestMediaTimestamp[String(currentIdea.id)] = {
              type: 'image',
              timestamp: Date.now()
            };
            persistSessionState();
          }

          // If visual/text was refined, invalidate any cached storyboard data
          // This covers the case where a format was converted to video and has stale storyboard cache
          if (refinedData.visual || refinedData.text) {
            invalidateStoryboard(currentIdea.id);
          }

          // If video format, sync updated scenes to storyboard state
          const hasUpdatedScenes = supportsVideoWorkflow(currentIdea.format) && refinedData.scenes;
          if (hasUpdatedScenes) {
            console.log('[IdeaCanvas] Syncing updated scenes to storyboard:', refinedData.scenes.length);
            // IMPORTANT: Also update meta.storyboard.scenes so loadFromIdea picks up the new scenes
            // (loadFromIdea prioritizes meta.storyboard.scenes over idea.scenes)
            if (!currentIdea.meta) currentIdea.meta = {};
            if (!currentIdea.meta.storyboard) currentIdea.meta.storyboard = {};
            currentIdea.meta.storyboard.scenes = refinedData.scenes;

            // CRITICAL: Invalidate stale cache before reloading
            // Without this, loadFromIdea() early-returns and ignores the new scenes
            invalidateStoryboard(currentIdea.id);
            loadFromIdea(currentIdea);
          }

          currentIdea.isRefined = true;

          // TASK 75 FIX: Re-initialize edit state with refined data
          // This resets dirty tracking and makes the refined text the new "original"
          // so manual editing and "Discard Changes" work correctly after refinement
          initEditState(currentIdea);

          // Re-render canvas with updated data
          render(currentIdea);

          // Re-initialize scripts after render (critical for scene tab event handlers)
          initializeCanvasScripts(currentIdea);

          // Update the idea card in the dashboard grid (if visible) without full page reload
          if (window.IdeasModule?.updateIdeaCard) {
            window.IdeasModule.updateIdeaCard(currentIdea);
          }

          // Switch to SCENES tab for execution mode, otherwise IDEA tab
          const state = getRefineState();
          if (hasUpdatedScenes && state.currentMode === 'execution') {
            switchTab('scenes');
            if (window.Toast) window.Toast.success('Scenes refined - see SCENES tab');
          } else {
            switchTab('idea');
            if (window.Toast) window.Toast.success('Refinement applied');
          }
        },
        onError: (err) => {
          console.error('[IdeaCanvas] Accept refinement failed:', err);
          if (window.Toast) window.Toast.error('Failed to apply refinement');
        }
      });
      return;
    }

    // Discard refinement button (v2)
    if (e.target.closest('#preview-btn-discard')) {
      e.stopPropagation();
      handleDiscardRefinement({
        onDiscard: () => {
          console.log('[IdeaCanvas] Refinement discarded');
          // Reset to ready state
          initRefineTab(currentIdea).catch(err => {
            console.warn('[IdeaCanvas] Failed to reset refine tab:', err);
          });
        }
      });
      return;
    }

    // Preview headline navigation (v2) - nav buttons for alternative headlines
    const previewHeadlinePrev = e.target.closest('#preview-headline-prev, .refine-alt-prev');
    const previewHeadlineNext = e.target.closest('#preview-headline-next, .refine-alt-next');
    if (previewHeadlinePrev) {
      e.stopPropagation();
      handlePreviewHeadlineNav(-1);
      return;
    }
    if (previewHeadlineNext) {
      e.stopPropagation();
      handlePreviewHeadlineNav(1);
      return;
    }

    // Direct click on headline item to select it (v2)
    const headlineItem = e.target.closest('.refine-headline-item');
    if (headlineItem) {
      e.stopPropagation();
      const index = parseInt(headlineItem.dataset.index, 10);
      if (!isNaN(index)) {
        handleHeadlineSelect(index);
      }
      return;
    }

    // Headline merge mode radio button change (v2)
    const mergeRadio = e.target.closest('input[name="headline-merge-mode"]');
    if (mergeRadio) {
      handleHeadlineMergeModeChange(mergeRadio.value);
      return;
    }

    // Format conversion button (v2) - STEP 1: Select format (don't call API yet)
    const formatBtn = e.target.closest('.format-convert-btn');
    if (formatBtn) {
      e.stopPropagation();
      const targetFormat = formatBtn.dataset.format;
      if (targetFormat && currentIdea) {
        // Just select the format, show confirmation UI
        handleSelectConvertFormat(targetFormat, currentIdea);
      }
      return;
    }

    // Cancel format selection (v2) - clear selection, stay on page
    if (e.target.closest('#convert-btn-cancel')) {
      e.stopPropagation();
      handleCancelConvertFormat(currentIdea);
      return;
    }

    // Start conversion button (v2) - STEP 2: User confirmed, now call API
    if (e.target.closest('#convert-btn-start')) {
      e.stopPropagation();
      handleStartConversion(currentIdea, {
        onConversionPreview: (convertedData) => {
          console.log('[IdeaCanvas] Format conversion preview ready:', convertedData.newFormat);
          // Render the conversion preview UI
          const refineContainer = document.querySelector('.canvas-tab-content[data-tab="refine"]');
          if (refineContainer) {
            refineContainer.innerHTML = renderConversionPreview(currentIdea, convertedData);
          }
        },
        onError: (err) => {
          console.error('[IdeaCanvas] Format conversion failed:', err);
          if (window.Toast) window.Toast.error('Format conversion failed');
        }
      });
      return;
    }

    // Accept format conversion button (v2) - STEP 3: User accepted preview, apply
    if (e.target.closest('#convert-btn-accept')) {
      e.stopPropagation();
      handleAcceptConversion(currentIdea, {
        onConversionComplete: (convertedData, imageData, createdNew) => {
          // Update the current idea with converted data
          Object.assign(currentIdea, convertedData);
          // Reset headline navigation state for new format
          delete currentIdea._originalHeadline;
          delete currentIdea._originalSubline;
          currentIdea.currentHeadlineIndex = 0;

          // Get the image URL from response
          const newImageUrl = imageData?.url || convertedData?.imageUrl;
          if (newImageUrl) {
            currentIdea.imageUrl = newImageUrl;
            // CRITICAL: Register in GeneratorState.preloadedImages so getIdeaImageUrl() can find it
            if (!GeneratorState.preloadedImages) {
              GeneratorState.preloadedImages = {};
            }
            GeneratorState.preloadedImages[currentIdea.id] = newImageUrl;
            console.log('[IdeaCanvas] Registered image for new idea:', currentIdea.id);
          }

          // If converted to video, hydrate storyboard state with scenes
          if (currentIdea.format?.toLowerCase() === 'video') {
            // Ensure scenes are in meta for loadFromIdea
            if (!currentIdea.meta) currentIdea.meta = {};
            if (convertedData.scenes && !currentIdea.meta.scenes) {
              currentIdea.meta.scenes = convertedData.scenes;
              currentIdea.meta.structure = {
                style: convertedData.style,
                duration: convertedData.duration,
                scenes: convertedData.scenes
              };
            }
            setActiveIdeaId(String(currentIdea.id));
            loadFromIdea(currentIdea);
            console.log('[IdeaCanvas] Hydrated storyboard state for video conversion');
          }

          // Re-render canvas with updated data
          render(currentIdea);
          // Re-initialize scripts after render (critical for event handlers)
          initializeCanvasScripts(currentIdea);
          switchTab('idea');
          // Show appropriate toast based on whether new idea was created
          if (createdNew) {
            // Add new idea to dashboard grid (if IdeasModule available)
            if (window.IdeasModule && window.IdeasModule.addNewIdea) {
              window.IdeasModule.addNewIdea(currentIdea);
              console.log('[IdeaCanvas] Added new idea to dashboard grid');
            }
            if (window.Toast) window.Toast.success('New idea created! Check your collection.');
          } else {
            if (window.Toast) window.Toast.success(`Converted to ${convertedData.format}`);
          }
        },
        onError: (err) => {
          console.error('[IdeaCanvas] Accept conversion failed:', err);
          if (window.Toast) window.Toast.error('Failed to apply conversion');
        }
      });
      return;
    }

    // Discard format conversion button (v2) - reset to ready state
    if (e.target.closest('#convert-btn-discard')) {
      e.stopPropagation();
      handleDiscardRefinement({
        onDiscard: () => {
          console.log('[IdeaCanvas] Format conversion discarded');
          // Reset to ready state
          initRefineTab(currentIdea).catch(err => {
            console.warn('[IdeaCanvas] Failed to reset refine tab:', err);
          });
        }
      });
      return;
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  function init(gridId, canvasId) {
    gridContainer = document.getElementById(gridId);
    canvasContainer = document.getElementById(canvasId);
    canvasInner = document.getElementById('canvas-inner');

    if (!gridContainer || !canvasContainer) {
      console.warn('[IdeaCanvas] Grid or canvas container not found');
      return false;
    }

    context = gridId === 'ideas-grid' ? 'dashboard' : 'generator';

    canvasContainer.addEventListener('click', handleCanvasClick);

    onDocument('keydown', (e) => {
      if (e.key === 'Escape' && canvasContainer.classList.contains('active')) {
        close();
      }
    }, 'idea-canvas');

    initialized = true;
    console.log(`[IdeaCanvas] Initialized (${context} mode)`);
    return true;
  }

  // ============================================================================
  // OPEN/CLOSE
  // ============================================================================

  async function open(idea) {
    if (!initialized) {
      console.warn('[IdeaCanvas] Not initialized. Call init() first.');
      return;
    }

    // Ensure copy dropdown is initialized (needed for canvas view)
    initCopyPromptDropdown();

    // Clear any active selections - this hides the action bar automatically
    // The action bar listens to selection changes and hides when count is 0
    // Also sync to DOM to remove .selected class from cards
    if (context === 'dashboard') {
      ideasSelection.clear();
      ideasSelection.syncToDOM('.idea-card', 'data-id');
    } else if (context === 'generator') {
      harvestSelection.clear();
      harvestSelection.syncToDOM('.idea-card', 'data-id');
    }

    currentIdea = idea;
    savedScrollY = window.scrollY;

    // =========================================================================
    // REFACTOR-03: Subscribe to events for this idea with scoped cleanup
    // =========================================================================
    setupEventSubscriptions(idea.id);

    // =========================================================================
    // TASK 75: Initialize edit state for canvas editing
    // =========================================================================
    initEditState(idea);

    // =========================================================================
    // FIX: Reset refine state when opening new idea
    // This ensures format-specific modes are correct and no stale state persists
    // =========================================================================
    resetRefineState();

    // =========================================================================
    // V5: BRAND REHYDRATION
    // When opening an idea from Dashboard, restore brand context for Refine/Regenerate
    // =========================================================================
    const ideaBrandId = idea.brand_id || idea.brandId;
    if (ideaBrandId && !GeneratorState.currentBrand) {
      try {
        console.log('[IdeaCanvas] Rehydrating brand:', ideaBrandId);
        const result = await apiRequest('/api/brands');
        if (result.success && result.brands) {
          const brand = result.brands.find(b => b.id === ideaBrandId);
          if (brand) {
            GeneratorState.currentBrand = brand;
            console.log('[IdeaCanvas] Brand rehydrated:', brand.name);
          }
        }
      } catch (err) {
        console.warn('[IdeaCanvas] Brand rehydration failed:', err.message);
      }
    }

    // Store original headline for navigation cycling (format-specific)
    if (!idea._originalHeadline) {
      const format = (idea.format || 'print').toLowerCase();
      const meta = idea.meta || {};
      // Get format-specific headline field
      if (format === 'video') {
        idea._originalHeadline = idea.super || meta.super || idea.headline || '';
      } else if (format === 'social') {
        idea._originalHeadline = idea.hook || meta.hook || idea.headline || '';
      } else if (format === 'digital') {
        idea._originalHeadline = idea.cta || meta.cta || idea.headline || '';
      } else if (format === 'experiential') {
        idea._originalHeadline = idea.reveal || meta.reveal || idea.headline || '';
      } else if (format === 'campaign') {
        idea._originalHeadline = idea.campaignLine || meta.campaignLine || idea.headline || '';
      } else {
        idea._originalHeadline = idea.headline || '';
      }
      idea._originalSubline = idea.subline || '';
    }
    // Reset headline index when opening
    idea.currentHeadlineIndex = 0;

    gridContainer.style.display = 'none';
    canvasContainer.classList.add('active');

    // REFACTOR-03: Emit canvas opened event
    emit(UI_EVENTS.CANVAS_OPENED, { idea });

    const ideaId = String(idea.id);
    const isVideoFormat = supportsVideoWorkflow(idea.format);

    if (isVideoFormat) {
      setActiveIdeaId(ideaId);
      setSplitSettings(ideaId, 3, 2);
      loadFromIdea(idea);
    }

    render(idea);

    // Re-initialize scripts after DOM rebuild
    initializeCanvasScripts(idea);

    window.scrollTo(0, 0);
    console.log('[IdeaCanvas] Opened for idea:', idea.id);
  }

  function close() {
    if (!canvasContainer) return;

    // Check for unsaved changes (Task 75)
    if (isDirty()) {
      if (window.Modal && window.Modal.confirm) {
        window.Modal.confirm({
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Discard them?',
          confirmText: 'Discard',
          cancelText: 'Keep Editing',
          onConfirm: () => {
            performClose();
          }
        });
        return;
      } else if (!confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }

    performClose();
  }

  /**
   * Actually close the canvas (called after unsaved changes check)
   */
  function performClose() {
    // Remove visual indicators before closing
    if (canvasInner) {
      canvasInner.classList.remove('has-unsaved-changes');
    }

    // REFACTOR-03: Clean up event subscriptions
    cleanupEventSubscriptions();

    cleanupScope('idea-canvas');
    canvasContainer.classList.remove('active');
    gridContainer.style.display = '';
    window.scrollTo(0, savedScrollY);

    // REFACTOR-03: Emit canvas closed event before clearing currentIdea
    emit(UI_EVENTS.CANVAS_CLOSED, { ideaId: currentIdea?.id });

    currentIdea = null;

    // Reset edit state (Task 75)
    editState.originalIdea = null;
    editState.currentIdea = null;
    editState.dirtyFields.clear();
    editState.isEditing = false;
    headlineState.items = [];
    headlineState.currentIndex = 0;

    console.log('[IdeaCanvas] Closed');
  }

  function getCurrentIdea() {
    return currentIdea;
  }

  function isOpen() {
    return canvasContainer && canvasContainer.classList.contains('active');
  }

  // ============================================================================
  // HEADLINE HELPER
  // ============================================================================

  /**
   * Render headline section with optional alternative headlines navigation
   * Redesigned: Nav is inline after headline, no borders
   * @param {Object} idea - The idea object
   * @param {string} ideaId - The idea ID
   * @param {string} headlineLabel - The label for the headline (HEADLINE, SUPER, CAPTION, etc.)
   * @returns {string} HTML string
   */
  function renderCanvasHeadlineSection(idea, ideaId, headlineLabel, resolvedHeadline, resolvedSubline) {
    const format = (idea.format || 'print').toLowerCase();
    const meta = idea.meta || {};

    // Use resolved values if provided, otherwise extract from idea
    const headline = resolvedHeadline || idea.headline || '';
    // Filter out literal "null" string from subline
    const subline = resolvedSubline || ((idea.subline && idea.subline !== 'null') ? idea.subline : '');

    // Get format-specific alternatives
    let alternativeHeadlines = idea.alternativeHeadlines || [];
    if (format === 'video') {
      alternativeHeadlines = idea.alternativeSupers || meta.alternativeSupers || [];
    } else if (format === 'social') {
      alternativeHeadlines = idea.alternativeHooks || meta.alternativeHooks || [];
    } else if (format === 'digital') {
      alternativeHeadlines = idea.alternativeCTAs || meta.alternativeCTAs || [];
    } else if (format === 'experiential') {
      alternativeHeadlines = idea.alternativeReveals || meta.alternativeReveals || [];
    } else if (format === 'campaign') {
      alternativeHeadlines = idea.alternativeLines || meta.alternativeLines || [];
    }

    const hasAlternatives = alternativeHeadlines.length > 0;
    const currentIndex = idea.currentHeadlineIndex || 0;

    if (!hasAlternatives) {
      // Simple headline without navigation
      return `
        <div class="canvas-headline-block">
          <span class="canvas-block-label">${headlineLabel}</span>
          <div class="canvas-block-headline">"${escapeHtml(headline)}"</div>
          ${subline ? `<div class="canvas-block-subline">${escapeHtml(subline)}</div>` : ''}
        </div>
      `;
    }

    // Headline with inline navigation (nav after headline text on same row)
    return `
      <div class="canvas-headline-block">
        <span class="canvas-block-label">${headlineLabel}</span>
        <div class="canvas-headline-row">
          <div class="canvas-block-headline">"${escapeHtml(headline)}"</div>
          <div class="canvas-headline-nav-inline">
            <button class="canvas-headline-nav-btn canvas-headline-prev" data-idea-id="${ideaId}" title="Previous headline">&lt;</button>
            <span class="canvas-headline-counter">${currentIndex + 1}/${alternativeHeadlines.length + 1}</span>
            <button class="canvas-headline-nav-btn canvas-headline-next" data-idea-id="${ideaId}" title="Next headline">&gt;</button>
          </div>
        </div>
        ${subline ? `<div class="canvas-block-subline">${escapeHtml(subline)}</div>` : ''}
      </div>
    `;
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  function render(idea) {
    if (!canvasInner) return;

    const CategoryColors = getCategoryColors() || CategoryColorsFallback;

    const id = String(idea.id);
    const triggerCode = idea.triggerCode || idea.trigger || idea.trigger_code || 'A1';
    const categoryCode = triggerCode.charAt(0).toUpperCase();
    const color = CategoryColors[categoryCode] || '#000000';
    const format = (idea.format || 'print').toLowerCase();

    // Use video URL getter (checks cache, state, and idea object)
    const videoUrl = getIdeaVideoUrl(id) || idea.videoUrl || idea.video_url || null;
    const imageUrl = getIdeaImageUrl(id);

    const title = idea.title || idea.headline || 'Untitled';
    const theIdea = idea.idea || '';
    const whyItWorks = idea.whyItWorks || idea.explanation || idea.why_it_works || idea.mechanism || '';
    const isRefined = idea.isRefined || idea.is_refined || false;
    const isSaved = idea.saved || idea.is_saved || false;

    // Extract format-specific fields from idea or meta
    const meta = idea.meta || {};

    // Build headline and visual based on format
    let headline = idea.headline || '';
    let headlineLabel = idea.headlineLabel || 'HEADLINE';
    let visual = idea.visual || idea.text || '';
    let sectionLabel = idea.sectionLabel || 'VISUAL';
    // Filter out literal "null" string from subline
    let subline = (idea.subline && idea.subline !== 'null') ? idea.subline : '';

    // Format-specific field extraction and display
    if (format === 'video') {
      // VIDEO: headline = super, visual = scenes description
      headline = idea.super || meta.super || idea.headline || '';
      headlineLabel = 'SUPER';
      const scenes = idea.scenes || meta.scenes || meta.storyboard?.scenes || idea.structure?.scenes || [];
      if (scenes.length > 0) {
        visual = scenes.map(s => `Scene ${s.seq}: ${s.visual}`).join('\n\n');
        sectionLabel = 'SCENES';
      } else {
        // Fallback to text blob for ideas without scenes data
        visual = idea.visual || idea.text || '';
        sectionLabel = 'EXECUTION';
      }
      // Duration and style as additional info
      const duration = idea.duration || meta.duration || '';
      const style = idea.style || meta.style || '';
      if (duration || style) {
        const durationInfo = [duration, style].filter(Boolean).join(' • ');
        subline = durationInfo;
      }
    } else if (format === 'social') {
      // SOCIAL: Check if individual fields exist (new storage) or fallback to text blob (old ideas)
      const socialFormat = idea.socialFormat || meta.socialFormat || '';
      const hook = idea.hook || meta.hook || '';
      const patternInterrupt = idea.patternInterrupt || meta.patternInterrupt || '';
      const sequence = idea.sequence || meta.sequence || '';
      const sound = idea.sound || meta.sound || '';
      const caption = idea.caption || meta.caption || '';

      // Use hook as headline
      headline = hook || idea.headline || '';
      headlineLabel = 'HOOK';

      // Check if we have individual fields (new storage format)
      if (hook || sequence || patternInterrupt || sound) {
        // Build structured visual from individual fields
        const parts = [];
        if (socialFormat) parts.push(`FORMAT: ${socialFormat}`);
        if (patternInterrupt) parts.push(`PATTERN INTERRUPT:\n${patternInterrupt}`);
        if (sequence) {
          // Handle both string and array formats
          const seqText = Array.isArray(sequence)
            ? sequence.join('\n')
            : (typeof sequence === 'string' ? sequence.replace(/\s*\|\s*/g, '\n') : String(sequence));
          parts.push(`SEQUENCE:\n${seqText}`);
        }
        if (sound) parts.push(`SOUND: ${sound}`);
        visual = parts.join('\n\n');
        sectionLabel = 'EXECUTION';
      } else {
        // Fallback to text blob for old ideas
        visual = idea.visual || '';
        sectionLabel = 'EXECUTION';
      }
      subline = caption;
    } else if (format === 'digital') {
      // DIGITAL: Check if individual fields exist or fallback to text blob
      const sizes = idea.sizes || meta.sizes || '';
      const hook = idea.hook || meta.hook || '';
      const frames = idea.frames || meta.frames || '';
      const interaction = idea.interaction || meta.interaction || '';
      const cta = idea.cta || meta.cta || '';

      // Use CTA as headline
      headline = cta || idea.headline || '';
      headlineLabel = 'CTA';

      // Check if we have individual fields
      if (hook || frames || interaction) {
        const parts = [];
        if (sizes) parts.push(`SIZES: ${sizes}`);
        if (hook) parts.push(`HOOK:\n${hook}`);
        if (frames) {
          const framesText = Array.isArray(frames)
            ? frames.join('\n')
            : (typeof frames === 'string' ? frames.replace(/\s*\|\s*/g, '\n') : String(frames));
          parts.push(`ANIMATION:\n${framesText}`);
        }
        if (interaction) parts.push(`INTERACTION: ${interaction}`);
        visual = parts.join('\n\n');
        sectionLabel = 'EXECUTION';
      } else {
        visual = idea.visual || '';
        sectionLabel = 'EXECUTION';
      }
      subline = '';
    } else if (format === 'experiential') {
      // EXPERIENTIAL: Check if individual fields exist or fallback
      const scale = idea.scale || meta.scale || '';
      const location = idea.location || meta.location || '';
      const setup = idea.setup || meta.setup || '';
      const participation = idea.participation || meta.participation || '';
      const reveal = idea.reveal || meta.reveal || '';
      const capture = idea.capture || meta.capture || '';
      const takeaway = idea.takeaway || meta.takeaway || '';

      // Use reveal as headline
      headline = reveal || idea.headline || '';
      headlineLabel = 'REVEAL';

      // Check if we have individual fields
      if (setup || participation || reveal || capture) {
        const parts = [];
        if (scale) parts.push(`SCALE: ${scale}`);
        if (location) parts.push(`LOCATION: ${location}`);
        if (setup) parts.push(`SETUP:\n${setup}`);
        if (participation) parts.push(`PARTICIPATION:\n${participation}`);
        if (capture) parts.push(`CAPTURE:\n${capture}`);
        if (takeaway) parts.push(`TAKEAWAY: ${takeaway}`);
        visual = parts.join('\n\n');
        sectionLabel = 'ACTIVATION';
      } else {
        visual = idea.visual || '';
        sectionLabel = 'ACTIVATION';
      }
      subline = '';
    } else if (format === 'campaign') {
      // CAMPAIGN: Check if individual fields exist or fallback
      const campaignLine = idea.campaignLine || meta.campaignLine || '';
      const manifesto = idea.manifesto || meta.manifesto || '';
      const heroExecution = idea.heroExecution || meta.heroExecution || '';
      const systemLogic = idea.systemLogic || meta.systemLogic || '';
      const extensions = idea.extensions || meta.extensions || '';
      const participation = idea.participation || meta.participation || '';
      const earned = idea.earned || meta.earned || '';

      // Use campaign line as headline
      headline = campaignLine || idea.headline || '';
      headlineLabel = 'CAMPAIGN LINE';

      // Check if we have individual fields
      if (manifesto || heroExecution || systemLogic || extensions) {
        const parts = [];
        if (manifesto) parts.push(`MANIFESTO:\n${manifesto}`);
        if (heroExecution) parts.push(`HERO EXECUTION:\n${heroExecution}`);
        if (systemLogic) parts.push(`SYSTEM LOGIC:\n${systemLogic}`);
        if (extensions) {
          const extText = Array.isArray(extensions)
            ? extensions.join('\n')
            : (typeof extensions === 'string' ? extensions.replace(/\s*\|\s*/g, '\n') : String(extensions));
          parts.push(`EXTENSIONS:\n${extText}`);
        }
        if (participation) parts.push(`PARTICIPATION:\n${participation}`);
        if (earned) parts.push(`EARNED MEDIA:\n${earned}`);
        visual = parts.join('\n\n');
        sectionLabel = 'PLATFORM';
      } else {
        visual = idea.visual || '';
        sectionLabel = 'PLATFORM';
      }
      subline = '';
    }

    const sourceFragment = idea.sourceFragment || '';
    const sourceTruth = idea.sourceTruth || '';

    // BUG 4 FIX: Improved brief handling for Dashboard mode
    // Prioritize idea.meta.brief over GeneratorState.brief (session context)
    // Handle both string (legacy) and object (current) formats
    // NOTE: Use imported GeneratorState directly (not window.GeneratorState) for reliable access
    const briefRaw = idea.meta?.brief || GeneratorState?.brief || {};
    let product = '';
    let benefit = '';

    // DEBUG: Log brief sources
    console.log('[IdeaCanvas] Brief debug:', {
      'idea.meta?.brief': idea.meta?.brief,
      'GeneratorState?.brief': GeneratorState?.brief,
      briefRaw
    });

    if (typeof briefRaw === 'string') {
      // Legacy format: brief was stored as just product name
      product = briefRaw;
    } else if (typeof briefRaw === 'object' && briefRaw !== null) {
      product = briefRaw.product || '';
      benefit = briefRaw.benefit || '';
    }

    console.log('[IdeaCanvas] Extracted product/benefit:', { product, benefit });

    // DEBUG: Log data to help identify missing fields
    console.log('[IdeaCanvas] Rendering with data:', {
      id,
      theIdea: theIdea ? theIdea.substring(0, 50) + '...' : '(empty)',
      whyItWorks: whyItWorks ? whyItWorks.substring(0, 50) + '...' : '(empty)',
      sourceFragment: sourceFragment ? sourceFragment.substring(0, 50) + '...' : '(empty)',
      sourceTruth: sourceTruth ? sourceTruth.substring(0, 50) + '...' : '(empty)',
      product: product || '(empty)',
      benefit: benefit || '(empty)',
      briefSource: idea.meta?.brief ? 'idea.meta.brief' : (window.GeneratorState?.brief ? 'GeneratorState' : 'none'),
      rawIdea: idea
    });

    const triggerName = idea.triggerName || idea.trigger_name || '';

    canvasInner.style.setProperty('--cat-color', color);

    // Task 102: Determine which media to show based on latest generation timestamp
    // This ensures newly generated images display even when a video already exists
    const latestMedia = GeneratorState.latestMediaTimestamp?.[id];
    let showVideo = false;
    let showImage = false;

    if (latestMedia?.type === 'video' && videoUrl) {
      showVideo = true;
    } else if (latestMedia?.type === 'image' && imageUrl) {
      showImage = true;
    } else if (videoUrl) {
      // Fallback: show video if exists (legacy behavior for pre-existing data)
      showVideo = true;
    } else if (imageUrl) {
      showImage = true;
    }

    // Build media HTML
    // Check if format supports image generation for showing media buttons
    const canGenerateImage = supportsImageGeneration(format);

    // Helper to render big media action buttons
    const renderMediaButtons = (hasImg, imgUrl) => {
      if (!canGenerateImage) return '';
      return `
        <div class="canvas-media-buttons">
          <button class="btn-media-action btn-generate-image" data-idea-id="${id}">
            ${Icons.generateImage({ size: 18 })}
            <span>Generate Image</span>
          </button>
          ${hasImg ? `
          <button class="btn-media-action btn-edit-image" data-id="${id}">
            ${Icons.editFilled({ size: 18 })}
            <span>Edit Image</span>
          </button>
          <button class="btn-media-action btn-create-video" data-id="${id}" data-image-url="${imgUrl}">
            ${Icons.makeVideo({ size: 18 })}
            <span>Make Video</span>
          </button>
          ` : ''}
        </div>
      `;
    };

    let imageHtml = '';
    if (showVideo) {
      imageHtml = `
        <div class="canvas-video-wrapper">
          <video src="${videoUrl}" class="canvas-main-video" controls preload="metadata">
            Your browser doesn't support video playback.
          </video>
          <div class="canvas-image-actions">
            ${renderHistoryButton(id)}
            <button class="btn-img-action btn-video-preview" data-url="${videoUrl}" title="View Fullscreen">
              ${Icons.fullscreen16({ size: 16 })}
            </button>
            <button class="btn-img-action btn-video-download" data-url="${videoUrl}" title="Download Video">
              ${Icons.download16({ size: 16 })}
            </button>
          </div>
          ${renderMediaButtons(!!imageUrl, imageUrl)}
        </div>
      `;
    } else if (showImage) {
      imageHtml = `
        <div class="canvas-image-wrapper">
          <img src="${imageUrl}" class="canvas-main-image" alt="Generated visual">
          <div class="canvas-image-actions">
            ${renderHistoryButton(id)}
            <button class="btn-img-action btn-preview" data-url="${imageUrl}" title="View Full">
              ${Icons.fullscreen16({ size: 16 })}
            </button>
            <button class="btn-img-action btn-download" data-url="${imageUrl}" title="Download">
              ${Icons.download16({ size: 16 })}
            </button>
          </div>
          ${renderMediaButtons(true, imageUrl)}
        </div>
      `;
    } else {
      imageHtml = `
        <div class="canvas-placeholder">
          <div class="canvas-placeholder-text">No media generated</div>
          ${renderMediaButtons(false, '')}
        </div>
      `;
    }

    const hasImage = !!imageUrl;
    const showImageTab = supportsImageGeneration(format);
    const isVideoFormat = supportsVideoWorkflow(format);
    const shortTitle = title.length > 40 ? title.substring(0, 40) + '...' : title;

    canvasInner.innerHTML = `
      <!-- Header -->
      <div class="canvas-header">
        <div class="canvas-header-left">
          <button class="btn-canvas-back" id="canvas-back-btn-inner">
            <span class="back-arrow">&larr;</span>
          </button>
          <div class="canvas-header-divider"></div>
          <div class="canvas-meta">
            <span class="canvas-trigger-badge">${triggerCode}</span>
            <span class="canvas-format-badge">${format.toUpperCase()}</span>
            ${isRefined ? '<span class="canvas-refined-badge">REFINED</span>' : ''}
          </div>
          <div class="canvas-header-divider"></div>
          <span class="canvas-header-title" title="${escapeHtml(title)}">${escapeHtml(shortTitle)}</span>
        </div>
        <div class="canvas-header-actions idea-card-actions">
          <!-- TASK 75: Save changes button (hidden until dirty) -->
          <button class="canvas-action-btn canvas-save-changes-btn" style="display: none;" title="Save Changes">
            <span class="btn-text">SAVE</span>
          </button>
          <button class="canvas-action-btn idea-copy-btn" data-id="${id}" title="Copy Prompt">
            ${Icons.copy({ size: 16 })}
          </button>
          <button class="canvas-action-btn idea-save-btn ${isSaved ? 'saved' : ''}" data-id="${id}" title="${isSaved ? 'Unsave' : 'Save'}">
            <img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon star-black" alt="">
            <img src="/assets/icons/icon_0026_star-filled-white.png" class="pixel-icon star-white" alt="">
          </button>
          ${context === 'dashboard' ? `
            <button class="canvas-action-btn idea-delete-btn" data-id="${id}" title="Delete Idea">
              ${Icons.delete({ size: 16 })}
            </button>
          ` : ''}
        </div>
      </div>

      <!-- Two Columns -->
      <div class="canvas-columns">
        <div class="canvas-col-image">
          ${imageHtml}
        </div>
        <div class="canvas-col-details">
          <!-- Tab Navigation - horizontal row, dynamic width -->
          <div class="canvas-tab-nav">
            <div class="canvas-tab-grid ${isVideoFormat ? 'has-video-tabs' : 'basic-tabs'}">
              <button class="canvas-tab-card active" data-tab="idea">
                <img src="${TAB_ICONS.idea}" alt="">
                <span>Idea</span>
              </button>
              <button class="canvas-tab-card" data-tab="refine">
                <img src="${TAB_ICONS.refine}" alt="">
                <span>Refine Idea</span>
              </button>
              ${isVideoFormat ? `
              <button class="canvas-tab-card" data-tab="storyboard">
                <img src="${TAB_ICONS.storyboard}" alt="">
                <span>Storyboard</span>
              </button>
              <button class="canvas-tab-card" data-tab="scenes">
                <img src="${TAB_ICONS.scenes}" alt="">
                <span>Scenes</span>
              </button>
              <button class="canvas-tab-card" data-tab="videos">
                <img src="${TAB_ICONS.videos}" alt="">
                <span>Video</span>
              </button>
              ` : ''}
            </div>
          </div>

          <!-- Tab Contents -->
          <div class="canvas-tab-panels">
            <!-- IDEA Tab (TASK 75: Editable fields) -->
            <div class="canvas-tab-content active" data-tab="idea">
              <!-- THE IDEA section - editable, uses category color -->
              <div class="canvas-idea-section">
                <div class="canvas-idea-header">
                  ${Icons.bulb({ size: 18, className: 'canvas-bulb-icon' })}
                  <span class="canvas-idea-label">THE IDEA</span>
                </div>
                <p class="canvas-idea-text canvas-editable ${!theIdea ? 'empty-placeholder' : ''}"
                   data-editable="true"
                   data-field="idea"
                   data-multiline="true">${theIdea ? escapeHtml(theIdea) : '<span class="empty-placeholder">Click to add a one-sentence concept...</span>'}</p>
              </div>

              <!-- Headline Manager (TASK 75: Replaces old headline section) -->
              ${renderHeadlineManager(idea, id, headlineLabel)}

              <!-- Visual section - editable -->
              <div class="canvas-visual-block">
                <span class="canvas-block-label">${sectionLabel}</span>
                <div class="canvas-block-body canvas-editable ${!visual ? 'empty-placeholder' : ''}"
                     data-editable="true"
                     data-field="visual"
                     data-multiline="true">${visual ? escapeHtml(visual) : '<span class="empty-placeholder">Click to add visual description...</span>'}</div>
              </div>

              <!-- 2x2 Insight Grid (editable cells) -->
              <div class="canvas-insight-grid">
                <div class="canvas-insight-cell">
                  <span class="canvas-insight-label">WHY IT WORKS</span>
                  <div class="canvas-insight-body canvas-editable ${!whyItWorks ? 'empty-placeholder' : ''}"
                       data-editable="true"
                       data-field="whyItWorks"
                       data-multiline="true">${whyItWorks ? escapeHtml(whyItWorks) : '<span class="empty-placeholder">Click to explain...</span>'}</div>
                </div>
                <div class="canvas-insight-cell">
                  <span class="canvas-insight-label">SPARKED BY</span>
                  <div class="canvas-insight-body canvas-editable ${!sourceFragment ? 'empty-placeholder' : ''}"
                       data-editable="true"
                       data-field="sourceFragment"
                       data-multiline="true">${sourceFragment ? '"' + escapeHtml(sourceFragment) + '"' : '<span class="empty-placeholder">Click to add...</span>'}</div>
                </div>
                <div class="canvas-insight-cell">
                  <span class="canvas-insight-label">THE TRUTH</span>
                  <div class="canvas-insight-body canvas-editable ${!sourceTruth ? 'empty-placeholder' : ''}"
                       data-editable="true"
                       data-field="sourceTruth"
                       data-multiline="true">${sourceTruth ? escapeHtml(sourceTruth) : '<span class="empty-placeholder">Click to add...</span>'}</div>
                </div>
                <div class="canvas-insight-cell">
                  <span class="canvas-insight-label">BRIEF</span>
                  <div class="canvas-insight-body">
                    ${product ? `<div class="brief-line"><strong>Product:</strong> ${escapeHtml(product)}</div>` : ''}
                    ${benefit ? `<div class="brief-line"><strong>Benefit:</strong> ${escapeHtml(benefit)}</div>` : ''}
                    ${!product && !benefit ? '<span class="empty-placeholder">No brief data</span>' : ''}
                  </div>
                </div>
              </div>

              <!-- Notes Section (TASK 75: Notepad for user annotations) -->
              ${renderNotesSection(idea)}
            </div>

            ${isVideoFormat ? `
            <!-- STORYBOARD Tab (includes Panel Library) -->
            <div class="canvas-tab-content" data-tab="storyboard">
              <div class="prompt-tip-blue">
                <span class="tip-icon-blue">i</span>
                <span class="tip-text-blue">Generate a grid image, then split it in the Panel Library below.</span>
              </div>
              <div class="storyboard-settings">
                <div class="storyboard-setting-group">
                  <label class="storyboard-setting-label">PANELS</label>
                  <select id="canvas-storyboard-panel-count" class="storyboard-setting-select">
                    <option value="4">4 panels (2x2)</option>
                    <option value="6" selected>6 panels (3x2)</option>
                    <option value="9">9 panels (3x3)</option>
                    <option value="12">12 panels (4x3)</option>
                  </select>
                </div>
                <div class="storyboard-setting-group">
                  <label class="storyboard-setting-label">ASPECT</label>
                  <select id="canvas-storyboard-aspect" class="storyboard-setting-select">
                    <option value="3:2">3:2 (Landscape)</option>
                    <option value="1:1" selected>1:1 (Square)</option>
                    <option value="2:3">2:3 (Portrait)</option>
                    <option value="16:9">16:9 (Wide)</option>
                  </select>
                </div>
                <button class="btn-gen-storyboard" id="btn-canvas-gen-storyboard">Generate Storyboard</button>
              </div>
              <div class="prompt-section">
                <textarea class="prompt-editor" id="canvas-storyboard-textarea" spellcheck="false"></textarea>
                <div class="storyboard-actions">
                  <button class="btn-copy-inline" data-target="canvas-storyboard-textarea">COPY</button>
                </div>
              </div>

              <!-- Panel Library Section (merged from panels tab) -->
              <div class="storyboard-panels-divider"></div>
              <div class="storyboard-panels-header">
                <span class="panel-library-title">PANEL LIBRARY</span>
              </div>
              <div class="panel-source-section" id="canvas-panel-source-section"></div>
              <div class="panel-library-grid" id="canvas-panel-library-grid"></div>
              <div class="panel-library-actions" id="canvas-panel-library-actions"></div>
            </div>

            <!-- SCENES Tab -->
            <div class="canvas-tab-content" data-tab="scenes">
              <div class="prompt-tip-blue">
                <span class="tip-icon-blue">i</span>
                <span class="tip-text-blue">Generate production-ready prompts for Runway, Veo 2, Pika, Kling, or Sora.</span>
              </div>
              <div class="video-scenes-container" id="canvas-video-scenes-container">
                <div class="video-scenes-placeholder" id="canvas-video-scenes-placeholder">
                  <p>Click below to generate video prompts with:</p>
                  <ul>
                    <li>Detailed visual descriptions</li>
                    <li>Camera movements</li>
                    <li>Audio/music direction</li>
                    <li>Timing per scene</li>
                  </ul>
                </div>
              </div>
              <div class="video-scenes-footer">
                <div class="video-scenes-actions">
                  <button class="btn-scene-action btn-primary-action" id="btn-canvas-generate-scenes">
                    <span class="btn-icon">⚡</span>
                    <span class="btn-text">GENERATE SCENES WITH AI</span>
                  </button>
                  <button class="btn-scene-action" id="btn-canvas-import-scenes">
                    <span class="btn-icon">&#8595;</span>
                    <span class="btn-text">IMPORT PANELS</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- VIDEOS Tab -->
            <div class="canvas-tab-content" data-tab="videos">
              <div class="prompt-tip-blue">
                <span class="tip-icon-blue">i</span>
                <span class="tip-text-blue">All videos generated from your scenes appear here. Preview, download, or delete.</span>
              </div>
              <div id="canvas-videos-content"></div>
            </div>
            ` : ''}

            <!-- REFINE Tab - populated dynamically by initRefineTab (V2) -->
            <div class="canvas-tab-content" data-tab="refine"></div>
          </div>
        </div>
      </div>
    `;
  }

  // ============================================================================
  // REFRESH & UPDATE
  // ============================================================================

  /**
   * Re-initializes scripts and event listeners after a render.
   * Critical for ensuring buttons work after DOM updates.
   */
  function initializeCanvasScripts(idea) {
    if (!idea) return;

    // =========================================================================
    // TASK 75: Initialize edit mode event listeners
    // =========================================================================

    // Headline add input - Enter to add
    const headlineInput = canvasInner?.querySelector('.headline-add-input');
    if (headlineInput) {
      attachHeadlineInputListener(headlineInput);
    }

    // Headline text contenteditable - blur/input to update
    const headlineTexts = canvasInner?.querySelectorAll('.headline-text');
    headlineTexts?.forEach(el => {
      if (el._listenerAttached) return;
      el._listenerAttached = true;

      el.addEventListener('blur', () => {
        const index = parseInt(el.dataset.index, 10);
        if (!isNaN(index)) {
          // Remove quotes if present
          let newText = el.textContent.trim();
          if (newText.startsWith('"')) newText = newText.slice(1);
          if (newText.endsWith('"')) newText = newText.slice(0, -1);
          handleUpdateHeadlineText(index, newText);
        }
      });

      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          el.blur();
        }
        if (e.key === 'Escape') {
          // Revert to original
          const index = parseInt(el.dataset.index, 10);
          if (!isNaN(index) && headlineState.items[index]) {
            el.textContent = '"' + headlineState.items[index].text + '"';
          }
          el.blur();
        }
      });
    });

    // Notes textarea - input to track changes
    const notesTextarea = canvasInner?.querySelector('.canvas-notes-input');
    if (notesTextarea && !notesTextarea._listenerAttached) {
      notesTextarea._listenerAttached = true;

      notesTextarea.addEventListener('input', () => {
        trackChange('notes', notesTextarea.value);
      });

      notesTextarea.addEventListener('blur', () => {
        trackChange('notes', notesTextarea.value.trim());
      });
    }

    // =========================================================================
    // Storyboard initialization (existing)
    // =========================================================================

    const isVideoFormat = supportsVideoWorkflow(idea.format);

    if (isVideoFormat) {
      // Force re-initialization of storyboard UI
      storyboardInitialized = false;

      // Reset container-level flag so initStoryboardUI() will re-attach handlers
      const scenesContainer = document.getElementById('canvas-video-scenes-container');
      if (scenesContainer) {
        delete scenesContainer.dataset.storyboardInitialized;
      }

      // Now initialize - handlers will be attached to new DOM elements
      initStoryboardUI();
      storyboardInitialized = true;
      console.log('[IdeaCanvas] Storyboard scripts re-initialized');
    }
  }

  function refresh() {
    if (currentIdea) {
      // Preserve current active tab before re-render
      const activeTabBtn = canvasContainer?.querySelector('.canvas-tab-card.active');
      const activeTab = activeTabBtn?.dataset?.tab || 'idea';

      render(currentIdea);
      initializeCanvasScripts(currentIdea);

      // Restore active tab after re-render
      if (activeTab !== 'idea') {
        switchTab(activeTab);
      }
    }
  }

  function updateIdea(updatedIdea) {
    currentIdea = updatedIdea;
    render(currentIdea);
    initializeCanvasScripts(currentIdea);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    init,
    open,
    close,
    refresh,
    updateIdea,
    getCurrentIdea,
    isOpen
  };
})();

// Expose to window for non-module contexts
window.IdeaCanvas = IdeaCanvas;
